import React from 'react';
import { App } from 'owa-application';
import { NativeContextMenu, useInitializeNativeContextMenuEvents } from 'native-context-menu';
import { SkipLinkControl } from 'owa-skip-link';
import type { SearchBoxContainerHandle } from 'owa-search';
import { getBootstrapOptions } from '../optionsRegistry';
import { observer } from 'owa-mobx-react';
import { OwaSuiteHeader } from 'owa-suite-header';
import { OwaHeader } from 'owa-header';
import { Module } from 'owa-workloads-module';
import { lazySetupAppModuleKeys } from './lazy/lazyFunctions';
import { useLazyKeydownHandler } from 'owa-hotkeys';
import { RebootModal } from 'owa-app-reboot';
import { NovaSharedDialogs } from 'owa-nova-shared-dialogs';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { ProjectionPopoutHost } from 'owa-projection-popout';
import { getCurrentModule } from 'owa-app-module-store';
import { RestoreItemsDialogFluentV9 } from 'owa-freeze-dry';
import { NovaContextProvider } from 'owa-nova';
import { AppBar, isAppBarEnabled } from 'owa-appbar';
import { AppRibbon } from 'owa-app-ribbon';
import { AppModuleContainer } from './AppModuleContainer';
import { AppBackground } from 'owa-app-background';
import { MsalAuthInfoBar } from 'owa-app-msal-messagebar';
import { RefreshToEnableScreenCaptureInfoBar } from 'owa-app-screencapture-messagebar';
import { DialogHost } from 'owa-dialog/lib/components/DialogHost';
import { SignInStateChangedInfoBar } from 'owa-app-signinstatechanged-infobar';
import type { OwaHeaderProps, OwaSuiteHeaderProps } from 'owa-bootstrap';
import { isCapabilityEnabled } from 'owa-capabilities';
import { newHeaderCapability } from 'owa-capabilities-definitions/lib/newHeaderCapability';
import { BulkImportExportDialogs } from 'owa-bulk-transfer-engine-post-boot';
import { CopilotChatDrawerContainer } from './CopilotChatDrawerContainer';
import { bizchatDrawerCapability } from 'owa-capabilities-definitions/lib/bizchatDrawerCapability';
import { MsalAuthModalDialog } from 'owa-app-msal-modaldialog';
import { LinkWithEasidDialogLightning } from 'owa-cloud-cache-link';
import { useDeferredValueFlighted } from 'owa-react-hooks/lib/useDeferredValueFlighted';
import type { MailboxInfo } from 'owa-client-types';

export interface AppModuleProps {
    wrapSuiteHeader?: (
        suiteHeader: React.ReactNode,
        mecontrolLauncher?: React.FC<{
            mailboxInfo: MailboxInfo;
        }>
    ) => React.ReactNode;
}

const defaultExport: React.FunctionComponent<AppModuleProps> = observer(function AppModule(
    props: AppModuleProps
) {
    useLazyKeydownHandler(undefined, lazySetupAppModuleKeys.importAndExecute, undefined);

    // Defer the currentModule value; this will render the module switch in concurrent mode
    const currentModule = useDeferredValueFlighted(
        getCurrentModule() || Module.Mail, // default to the mail module
        'fwk-deferred-module-switch'
    );

    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion  -- (https://aka.ms/OWALintWiki)
     * Non-null assertions are dangerous, as they can hide bugs from strictness checks. Please remove this usage or replace this line with a justification.
     *	> Forbidden non-null assertion. */
    const appModuleProps = getBootstrapOptions(currentModule)!.appModuleProps;
    const searchBoxRef = React.useRef<SearchBoxContainerHandle>(null);
    const isSkipLinkEnabled = appModuleProps.getIsSkipLinkEnabled?.() ?? false;

    const renderSkipLinkControl = React.useCallback(() => {
        return isSkipLinkEnabled ? <SkipLinkControl currentModule={currentModule} /> : undefined;
    }, [isSkipLinkEnabled, currentModule]);

    const headerInfo = appModuleProps.getHeaderInfo?.(searchBoxRef);
    const headerProps = headerInfo?.props;
    const isNewHeaderEnabled = isCapabilityEnabled(newHeaderCapability);
    const isCopilotChatDrawerEnabled = isCapabilityEnabled(bizchatDrawerCapability);

    if (headerProps && props.wrapSuiteHeader) {
        headerProps.wrapSuiteHeader = props.wrapSuiteHeader;
    }

    if (isNewHeaderEnabled && headerProps) {
        headerProps.currentModule = currentModule;
    }

    const renderHeader = React.useCallback(() => {
        const OverrideHeader = appModuleProps.overrideHeader?.();

        if (isHostAppFeatureEnabled('hideAppSuiteHeader')) {
            // If the host app has the hideAppSuiteHeader feature enabled, we don't want to render the header at all.
            return undefined;
        }

        if (OverrideHeader) {
            return <OverrideHeader />;
        }

        if (headerProps) {
            // If the headerProps are defined, we want to render the OwaSuiteHeader or OwaHeader based on the feature flag.
            return isNewHeaderEnabled ? (
                <OwaHeader {...(headerProps as OwaHeaderProps)} />
            ) : (
                <OwaSuiteHeader {...(headerProps as OwaSuiteHeaderProps)} />
            );
        }

        return undefined;
    }, [
        currentModule,
        headerProps?.searchAlignmentWidth,
        headerProps?.enableResponsiveLayout,
        headerInfo?.changes,
        isNewHeaderEnabled,
    ]);

    useInitializeNativeContextMenuEvents();

    return (
        <NovaContextProvider>
            <AppBackground />
            <App renderHeader={renderHeader} renderSkipLinkControl={renderSkipLinkControl}>
                {isAppBarEnabled() && <AppBar />}
                {appModuleProps.ribbonProps ? (
                    <AppModuleContainer>
                        {appModuleProps.overrideRibbon?.().isOverrideRibbon ? (
                            appModuleProps.overrideRibbon?.().overrideModule
                        ) : (
                            <AppRibbon {...appModuleProps.ribbonProps} />
                        )}
                        <SignInStateChangedInfoBar />
                        <MsalAuthInfoBar />
                        <RefreshToEnableScreenCaptureInfoBar />
                        <MsalAuthModalDialog />
                        {isCopilotChatDrawerEnabled ? (
                            <CopilotChatDrawerContainer
                                containerStyles={appModuleProps.getCopilotChatDrawerContainerStyles?.()}
                            >
                                {appModuleProps.renderModule(searchBoxRef)}
                            </CopilotChatDrawerContainer>
                        ) : (
                            appModuleProps.renderModule(searchBoxRef)
                        )}
                    </AppModuleContainer>
                ) : (
                    appModuleProps.renderModule(searchBoxRef)
                )}
                {isFeatureEnabled('fwk-reboot-notif') && <RebootModal />}
                <NovaSharedDialogs />
                <ProjectionPopoutHost />
                {isFeatureEnabled('fwk-freezeDry') && <RestoreItemsDialogFluentV9 />}
            </App>
            {isFeatureEnabled('cmp-dialogs-no-new-root') && <DialogHost />}
            {isFeatureEnabled('fwk-contextMenu') && <NativeContextMenu />}
            {isFeatureEnabled('acct-pstFileSupport') && <BulkImportExportDialogs />}
            {isFeatureEnabled('settings-link-easid-accounts') && <LinkWithEasidDialogLightning />}
        </NovaContextProvider>
    );
},
'AppModule');
export default defaultExport;
